import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Card,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Spinner,
  EmptyState,
  Icon,
} from "@shopify/polaris";
import { PlusIcon } from "@shopify/polaris-icons";
import { AppDispatch, RootState } from "configureStore";
import { setSelectedDelivery } from "../deliveries.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { Delivery, DeliveryStatus } from "types/Delivery";
import DeliveryCard from "./DeliveryCard";
import { buildDeliverySummary } from "../deliverySummary";
import { summaryRowTitle } from "features/fulfillment/orderLineRows";
import DataTable from "components/datatable/Datatable";

interface DeliveriesSectionProps {
  orderId: string;
  companyId: string;
}

const getStatusBadgeTone = (
  status: DeliveryStatus
): "info" | "success" | "warning" | "critical" | "attention" => {
  switch (status) {
    case "draft":
      return "info";
    case "planned":
      return "attention";
    case "shipped":
      return "warning";
    case "delivered":
      return "success";
    case "cancelled":
      return "critical";
    default:
      return "info";
  }
};

export default function DeliveriesSection({ orderId, companyId }: DeliveriesSectionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  // Get order entity with deliveries from order container slice
  const { orderEntity, isLoading } = useSelector(
    (state: RootState) => state.orderContainerReducer
  );

  const deliveries = (orderEntity?.deliveries || []) as Delivery[];
  const orderItems = orderEntity?.items || [];
  const productGroupLines = useMemo(
    () => orderEntity?.productGroups || [],
    [orderEntity?.productGroups]
  );

  const refreshDeliveries = () => {
    if (companyId && orderId) {
      dispatch(fetchOrderDetails({ companyId, orderId }));
    }
  };

  // Get reservations from order entity
  const reservations = orderEntity?.reservations || [];

  // Ordered | Reserved | Delivered | Remaining
  const deliverySummary = useMemo(
    () =>
      buildDeliverySummary(
        orderItems,
        productGroupLines,
        reservations,
        deliveries
      ),
    [orderItems, productGroupLines, deliveries, reservations]
  );

  const handleCreateDelivery = () => {
    dispatch(setModalxComponent("DeliveryForm"));
    dispatch(setModalxComponentProps({ orderId, mode: "create" }));
  };

  const handleEditDelivery = (delivery: Delivery) => {
    dispatch(setSelectedDelivery(delivery));
    dispatch(setModalxComponent("DeliveryForm"));
    dispatch(
      setModalxComponentProps({ orderId, deliveryId: delivery.id, mode: "edit" })
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <Spinner size="large" />
        </div>
      </Card>
    );
  }

  // Prepare summary for DataTable with remaining column
  const summaryCollection = deliverySummary.map((item) => {
    // Round to 2 decimal places to avoid floating point precision issues
    const remaining = Math.round((item.ordered - item.delivered) * 100) / 100;
    return {
      ...item,
      productTitle: summaryRowTitle(item, t),
      reserved: Math.round(item.reserved * 100) / 100,
      remaining,
      // tone for badge styling
      tone: remaining <= 0 ? "success" : "attention",
    };
  });

  return (
    <BlockStack gap="400">
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingSm">
            {t("deliveries")}
          </Text>
          <Button onClick={handleCreateDelivery}>
            <Icon source={PlusIcon as any} />
            {t("deliveries_create")}
          </Button>
        </InlineStack>
      </Card>

      {/* Delivery Summary */}
      {(orderItems.length > 0 || productGroupLines.length > 0) && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("deliveries_summary")}
            </Text>
            <DataTable
              collection={summaryCollection}
              columns={[
                { key: "sku", name: t("sku") },
                { key: "productTitle", name: t("product_name") },
                { key: "ordered", name: t("ordered"), type: "number" },
                { key: "reserved", name: t("reserved"), type: "number" },
                { key: "delivered", name: t("delivered"), type: "number" },
                { key: "remaining", name: t("remaining"), type: "badge" },
              ]}
              isTotalsDisabled
            />
          </BlockStack>
        </Card>
      )}

      {!deliveries || deliveries.length === 0 ? (
        <Card>
          <EmptyState
            heading={t("deliveries_no_deliveries")}
            image=""
            action={{
              content: t("deliveries_create_first"),
              onAction: handleCreateDelivery,
            }}
          >
            <p>{t("deliveries_no_deliveries_description")}</p>
          </EmptyState>
        </Card>
      ) : (
        <BlockStack gap="300">
          {deliveries.filter((d) => d && d.id).map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              onEdit={() => handleEditDelivery(delivery)}
              onRefresh={refreshDeliveries}
            />
          ))}
        </BlockStack>
      )}
    </BlockStack>
  );
}

export { getStatusBadgeTone };

